import React, { useState } from 'react';
import { ChevronDownIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { COMMON_VARIABLES, type CommonVariableName } from '../../../lib/types/template.types';

interface VariablePickerProps {
  onInsert: (variableName: string) => void;
  className?: string;
}

export function VariablePicker({ onInsert, className = '' }: VariablePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  const handleInsert = (variableName: string) => {
    onInsert(variableName);
    setIsOpen(false);

    // Show copied feedback
    setCopiedVar(variableName);
    setTimeout(() => setCopiedVar(null), 1500);
  };

  const variableEntries = Object.entries(COMMON_VARIABLES) as [CommonVariableName, string][];

  // Group variables by type
  const clientVars = variableEntries.filter(([key]) =>
    ['name', 'first_name', 'phone', 'email', 'age', 'gender', 'goal'].includes(key)
  );
  const dietitianVars = variableEntries.filter(([key]) =>
    ['dietitian_name', 'dietitian_phone', 'dietitian_organization'].includes(key)
  );
  const dynamicVars = variableEntries.filter(([key]) =>
    ['date', 'time'].includes(key)
  );

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium text-gray-700"
      >
        <span>Insert Variable</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
            <div className="p-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">
                Available Variables
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Click to insert into your template
              </p>
            </div>

            {/* Client Variables */}
            <div className="p-3 border-b border-gray-100">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Client Information
              </h4>
              <div className="space-y-1">
                {clientVars.map(([key, description]) => (
                  <VariableItem
                    key={key}
                    variableName={key}
                    description={description}
                    onInsert={handleInsert}
                    isCopied={copiedVar === key}
                  />
                ))}
              </div>
            </div>

            {/* Dietitian Variables */}
            <div className="p-3 border-b border-gray-100">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Your Information
              </h4>
              <div className="space-y-1">
                {dietitianVars.map(([key, description]) => (
                  <VariableItem
                    key={key}
                    variableName={key}
                    description={description}
                    onInsert={handleInsert}
                    isCopied={copiedVar === key}
                  />
                ))}
              </div>
            </div>

            {/* Dynamic Variables */}
            <div className="p-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Dynamic Values
              </h4>
              <div className="space-y-1">
                {dynamicVars.map(([key, description]) => (
                  <VariableItem
                    key={key}
                    variableName={key}
                    description={description}
                    onInsert={handleInsert}
                    isCopied={copiedVar === key}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface VariableItemProps {
  variableName: string;
  description: string;
  onInsert: (variableName: string) => void;
  isCopied: boolean;
}

function VariableItem({ variableName, description, onInsert, isCopied }: VariableItemProps) {
  return (
    <button
      type="button"
      onClick={() => onInsert(variableName)}
      className="w-full px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left flex items-start justify-between gap-2 group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <code className="text-xs font-mono bg-gray-100 text-[#5B8E87] px-2 py-0.5 rounded">
            {`{{${variableName}}}`}
          </code>
          {isCopied && (
            <span className="text-xs text-[#5B8E87] flex items-center gap-1">
              <CheckIcon className="w-3 h-3" />
              Inserted
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <CopyIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 mt-1" />
    </button>
  );
}
